import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import {
  Box,
  Chip,
  Paper,
  Typography,
  IconButton,
  Tooltip,
  ClickAwayListener,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';

/**
 * StructuredSearchField - A decorated search input that supports both free-text
 * and structured `field=value` queries with AND/OR logic.
 *
 * Features:
 * - Type freely for general search (sent as `q` param)
 * - Type `fieldName=value` for per-field filtering
 * - Completed conditions become chips that can be individually removed
 * - Clickable AND/OR toggle between chips to control cross-field logic
 * - Same field, multiple values → always OR (comma-joined)
 * - Different fields → AND or OR based on toggle
 * - Field name autocomplete suggestions shown on focus/typing
 */
// quantities and dates: searched as a from/to range in Advanced Search, not by typing one exact value
const RANGE_TYPES = ['NUMBER', 'INTEGER', 'DECIMAL', 'CURRENCY', 'DATE', 'DATETIME', 'TIMESTAMP', 'TIME'];

const StructuredSearchField = ({ fieldMetadata, onSearch, onClear, colors, placeholder, externalParams, presetText }) => {
  const [inputValue, setInputValue] = useState('');
  const [conditions, setConditions] = useState([]); // [{ field, label, value, external? }]
  const [logic, setLogic] = useState('AND'); // Cross-field logic: 'AND' or 'OR'
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef(null);
  const appliedExternalRef = useRef(null);

  // Sync external params (e.g., from NAVIGATE) into conditions
  useEffect(() => {
    if (!externalParams || Object.keys(externalParams).length === 0) {
      if (appliedExternalRef.current) {
        setConditions((prev) => prev.filter((c) => !c.external));
        appliedExternalRef.current = null;
      }
      return;
    }
    const key = JSON.stringify(externalParams);
    if (appliedExternalRef.current === key) return;
    appliedExternalRef.current = key;

    const newConditions = [];
    for (const [fieldName, value] of Object.entries(externalParams)) {
      if (!value) continue;
      const field = (fieldMetadata || []).find(
        (f) => f.fieldName === fieldName || (f.displayLabel || '').toLowerCase() === fieldName.toLowerCase()
      );
      newConditions.push({
        field: fieldName,
        label: field?.displayLabel || fieldName,
        value: String(value),
        external: true,
      });
    }
    setConditions((prev) => [...prev.filter((c) => !c.external), ...newConditions]);
  }, [externalParams, fieldMetadata]);

  // Text the screen searched for on the user's behalf ("Show it", FH-201): show it in the box.
  // An empty preset is the screen's "Clear search" (FH-81): empty the box and its conditions too.
  useEffect(() => {
    if (presetText?.text) setInputValue(presetText.text);
    // clears what was typed; a link's own filter (external) stays shown
    else if (presetText && presetText.text === '') { setInputValue(''); setConditions((prev) => prev.filter((c) => c.external)); }
  }, [presetText]);

  // Available fields for suggestions
  const availableFields = useMemo(() => {
    if (!fieldMetadata) return [];
    return fieldMetadata
      .filter((f) => {
        if (f.fieldName === 'id') return false;
        if (f.isVisibleInTable === false || f.isSearchable === false) return false;
        const ft = (f.fieldType || '').toUpperCase();
        if (ft === 'BOOLEAN' || ft === 'CHECKBOX') return false;
        // a from/to range (dates, numbers) is set in Advanced Search; one typed value can't express it
        if ((f.searchType || '').toUpperCase() === 'RANGE' || RANGE_TYPES.includes(ft)) return false;
        return true;
      })
      .map((f) => ({
        fieldName: f.fieldName,
        label: f.displayLabel || f.fieldName,
        fieldType: (f.fieldType || 'TEXT').toUpperCase(),
      }));
  }, [fieldMetadata]);

  // Filter suggestions based on current input text
  const filteredSuggestions = useMemo(() => {
    if (inputValue.includes('=') || inputValue.includes(':')) return [];
    if (!inputValue) return availableFields;   // all of them: the list scrolls (a 13th field, e.g. LPN, used to be unreachable)
    const lower = inputValue.toLowerCase();
    return availableFields.filter(
      (f) =>
        f.label.toLowerCase().includes(lower) ||
        f.fieldName.toLowerCase().includes(lower)
    );
  }, [inputValue, availableFields]);

  // Select a field from suggestions
  const handleSelectField = useCallback((field) => {
    setInputValue(`${field.fieldName}=`);
    setShowSuggestions(false);
    inputRef.current?.focus();
  }, []);

  // Build search params from conditions and fire onSearch
  // Same-field values → comma-joined (OR within field)
  // Cross-field logic → _logic param
  const triggerSearch = useCallback(
    (conds, freeText, crossLogic) => {
      const params = {};
      // Group by field — same field values are OR-joined with comma
      const byField = {};
      conds.forEach((c) => {
        if (!byField[c.field]) byField[c.field] = [];
        byField[c.field].push(c.value);
      });
      Object.entries(byField).forEach(([field, values]) => {
        params[field] = values.join(',');
      });
      if (freeText) {
        params.q = freeText;
      }
      // Only send _logic if there are multiple different fields and logic is OR
      const fieldCount = Object.keys(byField).length + (freeText ? 1 : 0);
      if (fieldCount > 1 && (crossLogic || logic) === 'OR') {
        params._logic = 'OR';
      }
      console.log('[StructuredSearch] triggerSearch params:', params);
      onSearch(params);
    },
    [onSearch, logic]
  );

  // Parse current input and add as condition or general search
  const handleSubmit = useCallback(() => {
    const raw = inputValue.trim();
    if (!raw) {
      // Enter in an emptied box clears the search, like the clear button (it used to keep the old one)
      if (conditions.length > 0) triggerSearch(conditions);
      else onClear();
      setShowSuggestions(false);
      return;
    }

    // Try to parse `field=value` or `field:value`
    const match = raw.match(/^(\w+)\s*[=:]\s*(.+)$/);
    if (match) {
      const [, fieldKey, fieldValue] = match;
      const field = availableFields.find(
        (f) =>
          f.fieldName.toLowerCase() === fieldKey.toLowerCase() ||
          f.label.toLowerCase() === fieldKey.toLowerCase()
      );
      if (field && fieldValue.trim()) {
        const newConditions = [
          ...conditions,
          { field: field.fieldName, label: field.label, value: fieldValue.trim() },
        ];
        setConditions(newConditions);
        setInputValue('');
        setShowSuggestions(false);
        triggerSearch(newConditions);
        return;
      }
    }

    // General free-text search
    triggerSearch(conditions, raw);
    setShowSuggestions(false);
  }, [inputValue, conditions, availableFields, triggerSearch, onClear]);

  // Remove a condition chip
  const removeCondition = useCallback(
    (index) => {
      const removed = conditions[index];
      const newConditions = conditions.filter((_, i) => i !== index);
      setConditions(newConditions);
      if (removed?.external) appliedExternalRef.current = null;
      if (newConditions.length === 0 && !inputValue.trim()) {
        onClear();
      } else {
        triggerSearch(newConditions, inputValue.trim() || undefined);
      }
    },
    [conditions, inputValue, onClear, triggerSearch]
  );

  // Clear everything
  const handleClearAll = useCallback(() => {
    setInputValue('');
    setConditions([]);
    setLogic('AND');
    setShowSuggestions(false);
    appliedExternalRef.current = null;
    onClear();
  }, [onClear]);

  // Toggle AND/OR and re-trigger search
  const toggleLogic = useCallback(() => {
    const newLogic = logic === 'AND' ? 'OR' : 'AND';
    setLogic(newLogic);
    if (conditions.length > 1) {
      triggerSearch(conditions, inputValue.trim() || undefined, newLogic);
    }
  }, [logic, conditions, inputValue, triggerSearch]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSubmit();
      }
      if (e.key === 'Backspace' && !inputValue && conditions.length > 0) {
        removeCondition(conditions.length - 1);
      }
      if (e.key === 'Escape') {
        setShowSuggestions(false);
      }
    },
    [inputValue, conditions, handleSubmit, removeCondition]
  );

  const hasContent = inputValue || conditions.length > 0;

  // Group conditions to detect when to show logic toggle
  // Show toggle between chips that belong to different fields
  const renderConditionsWithLogic = () => {
    const elements = [];
    for (let i = 0; i < conditions.length; i++) {
      const cond = conditions[i];

      // Add logic toggle between chips of different fields
      if (i > 0 && conditions[i - 1].field !== cond.field) {
        elements.push(
          <Tooltip key={`logic-${i}`} title={`Click to toggle (${logic === 'AND' ? 'switch to OR' : 'switch to AND'})`}>
            <Typography
              component="span"
              onClick={toggleLogic}
              sx={{
                fontSize: '0.6rem',
                fontWeight: 700,
                color: logic === 'OR' ? '#f59e0b' : '#3b82f6',
                cursor: 'pointer',
                px: 0.5,
                py: 0.125,
                borderRadius: '2px',
                bgcolor: logic === 'OR' ? 'rgba(245, 158, 11, 0.12)' : 'rgba(59, 130, 246, 0.12)',
                userSelect: 'none',
                lineHeight: 1,
                '&:hover': {
                  bgcolor: logic === 'OR' ? 'rgba(245, 158, 11, 0.25)' : 'rgba(59, 130, 246, 0.25)',
                },
              }}
            >
              {logic}
            </Typography>
          </Tooltip>
        );
      } else if (i > 0 && conditions[i - 1].field === cond.field) {
        // Same field → always OR
        elements.push(
          <Typography
            key={`or-${i}`}
            component="span"
            sx={{
              fontSize: '0.6rem',
              fontWeight: 700,
              color: colors.textMuted,
              px: 0.25,
              lineHeight: 1,
              userSelect: 'none',
            }}
          >
            or
          </Typography>
        );
      }

      elements.push(
        <Chip
          key={`${cond.field}-${i}`}
          label={`${cond.label} = ${cond.value}`}
          size="small"
          onDelete={() => removeCondition(i)}
          sx={{
            borderRadius: 0,
            fontSize: '0.7rem',
            height: 22,
            bgcolor: cond.external ? 'rgba(59, 130, 246, 0.12)' : 'rgba(34, 197, 94, 0.12)',
            color: cond.external ? '#3b82f6' : '#22c55e',
            border: `1px solid ${cond.external ? 'rgba(59, 130, 246, 0.25)' : 'rgba(34, 197, 94, 0.25)'}`,
            '& .MuiChip-deleteIcon': {
              fontSize: 14,
              color: cond.external ? 'rgba(59, 130, 246, 0.5)' : 'rgba(34, 197, 94, 0.5)',
              '&:hover': { color: '#ef4444' },
            },
          }}
        />
      );
    }
    return elements;
  };

  return (
    <ClickAwayListener onClickAway={() => setShowSuggestions(false)}>
      <Box data-enter-keep="" sx={{ flex: 1, maxWidth: 500, position: 'relative' }}>
        {/* Main input container */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 0.5,
            border: `1px solid ${colors.border}`,
            borderRadius: 0,
            bgcolor: colors.background,
            px: 1,
            py: 0.25,
            minHeight: 34,
            '&:hover': { borderColor: '#22c55e' },
            '&:focus-within': { borderColor: '#22c55e' },
          }}
        >
          <SearchIcon sx={{ fontSize: 18, color: colors.textMuted, flexShrink: 0 }} />

          {/* Condition chips with logic toggles */}
          {renderConditionsWithLogic()}

          {/* Native input */}
          <input
            ref={inputRef}
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              setShowSuggestions(true);
            }}
            onKeyDown={handleKeyDown}
            onFocus={() => setShowSuggestions(true)}
            placeholder={
              conditions.length > 0
                ? 'Add filter...'
                : placeholder || 'Search... (type field=value for structured search)'
            }
            style={{
              flex: 1,
              minWidth: 100,
              border: 'none',
              outline: 'none',
              background: 'transparent',
              color: colors.textPrimary,
              fontSize: '0.8rem',
              padding: '4px 0',
              fontFamily: 'inherit',
            }}
          />

          {/* Action buttons */}
          {hasContent && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
              {inputValue && (
                <Tooltip title="Search (Enter)">
                  <IconButton
                    size="small"
                    onClick={handleSubmit}
                    sx={{ color: '#22c55e', p: 0.25 }}
                  >
                    <SearchIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Clear all">
                <IconButton
                  size="small"
                  onClick={handleClearAll}
                  sx={{ color: colors.textMuted, p: 0.25, '&:hover': { color: '#ef4444' } }}
                >
                  <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Box>

        {/* Field suggestions dropdown */}
        {showSuggestions && filteredSuggestions.length > 0 && (
          <Paper
            elevation={4}
            sx={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              zIndex: 20,
              bgcolor: colors.surface,
              border: `1px solid ${colors.border}`,
              borderTop: 'none',
              maxHeight: 220,
              overflow: 'auto',
              mt: 0,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                px: 1.5,
                py: 0.5,
                color: colors.textMuted,
                display: 'block',
                borderBottom: `1px solid ${colors.border}`,
                fontSize: '0.7rem',
              }}
            >
              Fields — click to filter or type field=value
            </Typography>
            {filteredSuggestions.map((field) => (
              <Box
                key={field.fieldName}
                onClick={() => handleSelectField(field)}
                sx={{
                  px: 1.5,
                  py: 0.5,
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  '&:hover': { bgcolor: 'rgba(34, 197, 94, 0.08)' },
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ color: colors.textPrimary, fontSize: '0.8rem' }}
                >
                  {field.label}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: colors.textMuted, fontSize: '0.65rem', fontFamily: 'monospace' }}
                >
                  {field.fieldName}
                </Typography>
              </Box>
            ))}
          </Paper>
        )}
      </Box>
    </ClickAwayListener>
  );
};

export default StructuredSearchField;
